import styled from '@emotion/styled';
import { Placement } from '@popperjs/core/lib/enums';
import React, { FC, useState } from 'react';
import { usePopper } from 'react-popper';
import { dsColors } from 'src/shared/ds/variables';
import { BodyTextXSmall } from '../Typography';

const overridePositionStyles = {
    left: {
        left: 6,
        transform: 'unset',
    },
    right: {
        left: 'unset',
        right: 12,
        transform: 'unset',
    },
    center: {},
};

const setPosition = (placement?: Placement) => {
    let styles = {};

    if (!placement) {
        return styles;
    }

    if (placement.includes('bottom')) {
        styles = {
            borderWidth: `0 8px 12px 8px;`,
            borderColor: `transparent transparent ${dsColors.neutral400} transparent`,
            top: -4,
            right: -8,
        };
    }
    if (placement.includes('top')) {
        styles = {
            borderWidth: `12px 8px 0 8px`,
            borderColor: `${dsColors.neutral400} transparent transparent transparent`,
            bottom: -4,
            right: -8,
        };
    }

    return { '&, &::before': styles };
};

const Container = styled.div({
    backgroundColor: dsColors.neutral400,
    padding: 12,
    paddingBottom: 13,
    borderRadius: 4,
    display: 'none',

    '&[data-show]': {
        display: 'block',
    },
});

const Arrow = styled.div({
    visibility: 'hidden',
    border: 0,

    '&, &::before': {
        position: 'absolute',
        width: 0,
        height: 0,
        borderStyle: 'solid',
    },
    '&::before': {
        visibility: 'visible',
        content: '""',
    },
});

interface TooltipProps {
    arrowPosition?: 'left' | 'center' | 'right';
    tooltip: string;
}

const showEvents = ['mouseenter', 'focus'];
const hideEvents = ['mouseleave', 'blur'];

export const Tooltip: FC<TooltipProps> = ({ tooltip, children, arrowPosition = 'center' }) => {
    const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
    const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
    const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        modifiers: [
            { name: 'arrow', options: { element: arrowElement, padding: 12 } },
            {
                name: 'offset',
                options: {
                    offset: [10, 8],
                },
            },
        ],
        placement: 'top-end',
    });

    const show = () => {
        popperElement?.setAttribute('data-show', '');
        update?.();
    };

    const hide = () => {
        popperElement?.removeAttribute('data-show');
    };

    const handleReferenceElementRef = (element: HTMLDivElement | null) => {
        if (element) {
            setReferenceElement(element);

            showEvents.forEach(event => {
                element.addEventListener(event, show);
            });

            hideEvents.forEach(event => {
                element.addEventListener(event, hide);
            });
        }
    };

    const popperPlacement = attributes?.popper?.['data-popper-placement'] as Placement;
    return (
        <>
            <div ref={handleReferenceElementRef}>{children}</div>
            <Container ref={setPopperElement} style={styles.popper} {...attributes.popper}>
                <BodyTextXSmall fontColor="white">{tooltip}</BodyTextXSmall>
                <Arrow
                    ref={setArrowElement}
                    style={{ ...styles.arrow, ...overridePositionStyles[arrowPosition] }}
                    css={[setPosition(popperPlacement)]}
                />
            </Container>
        </>
    );
};
